import { useRef, useEffect } from "react";

// Multi-select checkbox group for the preference filters (Job Types, Company
// Size, Investment Stage). "Select all" is derived, not stored: it's checked
// only when every option is selected, indeterminate when some are, and toggling
// it either selects all concrete options or clears them. Because "all" always
// expands to the concrete option values, the payload the backend receives stays
// a plain list of valid choices -- no sentinel value, fully backward compatible.
export default function CheckboxGroup({
    title,
    name,
    options,
    checkedValues,
    onChange,
    onClear,
    onSelectAll,
}) {
    const allSelected =
        options.length > 0 && options.every((option) => checkedValues.includes(option));
    const someSelected = checkedValues.length > 0 && !allSelected;

    // Native "indeterminate" is a DOM property, not an attribute React sets, so
    // drive it via a ref whenever the partial-selection state changes.
    const selectAllRef = useRef(null);
    useEffect(() => {
        if (selectAllRef.current) {
            selectAllRef.current.indeterminate = someSelected;
        }
    }, [someSelected]);

    const handleSelectAllChange = () => {
        if (allSelected) {
            onClear(name);
        } else {
            onSelectAll(name, options);
        }
    };

    return (
        <div>
            <div className="flex justify-between items-center mb-2">
                <h4 className="text-sm font-medium text-gray-900 dark:text-gray-100">{title}</h4>
                <div className="flex items-center gap-x-4">
                    <label
                        htmlFor={`${name}-select-all`}
                        className="flex items-center gap-x-2 cursor-pointer text-sm font-medium text-gray-700 dark:text-gray-300"
                    >
                        <input
                            id={`${name}-select-all`}
                            ref={selectAllRef}
                            type="checkbox"
                            checked={allSelected}
                            onChange={handleSelectAllChange}
                            className="h-4 w-4 text-blue-600 border-gray-300 dark:border-gray-600 dark:bg-gray-700 rounded focus:ring-blue-500"
                        />
                        Select all
                    </label>
                    <button
                        type="button"
                        onClick={() => onClear(name)}
                        className="text-sm font-medium text-blue-700 dark:text-blue-400 hover:text-blue-500 dark:hover:text-blue-300"
                    >
                        Clear
                    </button>
                </div>
            </div>
            <div className="p-4 border border-gray-200 dark:border-gray-700 rounded-lg space-y-3">
                <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-x-4 gap-y-3">
                    {options.map((option) => (
                        <div key={option} className="flex items-center gap-x-2">
                            <input
                                id={`${name}-${option}`}
                                name={name}
                                value={option}
                                type="checkbox"
                                checked={checkedValues.includes(option)}
                                onChange={onChange}
                                className="h-4 w-4 text-blue-600 border-gray-300 dark:border-gray-600 dark:bg-gray-700 rounded focus:ring-blue-500"
                            />
                            <label
                                htmlFor={`${name}-${option}`}
                                className="text-sm text-gray-700 dark:text-gray-300"
                            >
                                {option}
                            </label>
                        </div>
                    ))}
                </div>
            </div>
        </div>
    );
}
